import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import htmlParser from 'html-react-parser';

import { addLeadingSlash } from '@fundera/fundera-utilities';
import authorMapper from '@fundera/fundera-design-system/mapping/author-mapper';

import { AppContext } from '../../providers/AppContext';
import { getSchema, schemas } from '../../seo';

function Seo(props) {
  const {
    dateModified,
    datePublished,
    description,
    lang,
    meta,
    keywords,
    title,
    schema,
    url,
    image,
    opengraphUrl,
    twitterDescription,
    twitterTitle,
    twitterImage,
    pageContext,
  } = props;
  const context = useContext(AppContext);
  const metaDescription = description;
  const fdsAuthor =
    pageContext.post &&
    pageContext.post.fdsPage &&
    pageContext.post.fdsPage.pageSettings &&
    pageContext.post.fdsPage.pageSettings.byline;
  const authorBase =
    fdsAuthor ||
    (pageContext.post && pageContext.post.author) ||
    pageContext.author ||
    props.author ||
    {};
  const authorMap = authorMapper(authorBase);
  const formattedTitle = htmlParser(title);
  const seoImage =
    (image && image.sourceUrl) || context.seo.defaultImage || context.seo.logo;
  const siteUrl = props.siteUrl || context.seo.siteUrl;
  const finalTwitterDescription = twitterDescription || metaDescription;
  const finalTwitterTitle = twitterTitle || formattedTitle;
  const finalTwitterImage =
    (twitterImage && twitterImage.sourceUrl) || seoImage;

  const schemaProps = {
    title,
    dateModified,
    datePublished,
    image: seoImage,
    url,
    author: authorMap,
    logo: props.logo || context.seo.logo,
    siteName: props.siteName || context.seo.siteName,
    siteUrl,
    address: props.address || context.seo.address,
    brand: props.brand || context.seo.brand,
    socialLinks: props.socialLinks || context.seo.socialLinks,
    affiliation: props.affiliation || context.seo.affiliation,
  };
  const pageSchemas = getSchema(schema, schemaProps);

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang,
        }}
        title={formattedTitle}
        titleTemplate={formattedTitle}
        meta={[
          {
            name: `description`,
            content: metaDescription,
          },
          {
            property: `og:title`,
            content: formattedTitle,
          },
          {
            property: `og:description`,
            content: metaDescription,
          },
          {
            property: `og:type`,
            content: `website`,
          },
          {
            property: `og:image`,
            content: seoImage,
          },
          {
            property: `og:url`,
            content: opengraphUrl || `${siteUrl}${addLeadingSlash(url)}`,
          },
          {
            name: `author`,
            content: authorMap.name,
          },
          {
            name: `twitter:card`,
            content: `summary`,
          },
          {
            name: `twitter:site`,
            content:
              context.socialLinks &&
              context.socialLinks.twitter &&
              context.socialLinks.twitter.id,
          },
          {
            name: `twitter:creator`,
            content: authorMap.name,
          },
          {
            name: `twitter:title`,
            content: finalTwitterTitle,
          },
          {
            name: `twitter:description`,
            content: finalTwitterDescription,
          },
          {
            name: `twitter:image`,
            content: finalTwitterImage,
          },
          {
            name: `publish_date`,
            property: `og:publish_date`,
            content: datePublished,
          },
        ]
          .concat(
            keywords.length > 0
              ? {
                  name: `keywords`,
                  content: Array.isArray(keywords)
                    ? keywords.join(`, `)
                    : keywords,
                }
              : []
          )
          .concat(meta)}
      >
        {pageSchemas.map((s, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(s)}
          </script>
        ))}
      </Helmet>
    </>
  );
}

Seo.propTypes = {
  author: PropTypes.object,
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.array,
  keywords: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  schema: PropTypes.oneOf([
    schemas.ABOUT,
    schemas.ARTICLE,
    schemas.AUTHOR,
    schemas.GENERIC,
    schemas.HOMEPAGE,
  ]).isRequired,
  title: PropTypes.string.isRequired,
  opengraphUrl: PropTypes.string,
  twitterDescription: PropTypes.string,
  twitterTitle: PropTypes.string,
  twitterImage: PropTypes.shape({
    sourceUrl: PropTypes.string,
  }),
};

Seo.defaultProps = {
  lang: `en`,
  meta: [],
  keywords: [],
  author: null,
  pageContext: {},
  opengraphUrl: '',
  twitterDescription: '',
  twitterTitle: '',
  twitterImage: null,
};

export default Seo;
